import { useSyncExternalStore } from "react";

// 亮色 / 暗色主題(業主 2026-09-25:「右上角(鈴鐺旁邊) 追加暗色主題/亮色主題 轉換ICON」)。
//
// ── 真正說了算的是帳號 ─────────────────────────────────────────────
// 選擇存在帳號上(`users.theme`,`PUT /api/users/me/theme`),換一台電腦也跟著人走。
// 登入與開頁時由 AuthProvider 照 `/auth/me` 帶回來的值套用。
//
// ── 瀏覽器裡那一份只是「開頁先套用」的提示 ──────────────────────────
// `/auth/me` 回來之前畫面已經畫了;不先套一次,暗色的人每次開頁都會先閃一下亮色。
// 它**不是**設定本身:帳號上的值一回來就蓋過它。讀寫一律包 try/catch ——
// 無痕模式或封鎖網站資料時 localStorage 會丟例外,主題壞掉不該讓整頁掛掉。
//
// ── null = 沒選過,跟著系統設定 ───────────────────────────────────
// 那時 <html> 上不標 data-theme,tokens.css 的 prefers-color-scheme 那一段接手。

export type ThemeChoice = "light" | "dark" | null;

const HINT_KEY = "nebula.theme";

const listeners = new Set<() => void>();

function current(): ThemeChoice {
  if (typeof document === "undefined") return null;
  const value = document.documentElement.dataset.theme;
  return value === "light" || value === "dark" ? value : null;
}

/** 套用主題並記下開頁用的提示。null 拿掉標記,回到跟著系統設定。 */
export function applyTheme(choice: ThemeChoice): void {
  if (typeof document !== "undefined") {
    if (choice) document.documentElement.dataset.theme = choice;
    else delete document.documentElement.dataset.theme;
  }
  try {
    if (choice) window.localStorage.setItem(HINT_KEY, choice);
    else window.localStorage.removeItem(HINT_KEY);
  } catch {
    // 讀寫不了就算了:少的只是開頁那一下的提示。
  }
  listeners.forEach((listener) => listener());
}

/** 開頁用的提示。沒有、讀不到或看不懂都回 null。 */
export function readThemeHint(): ThemeChoice {
  try {
    const value = window.localStorage.getItem(HINT_KEY);
    return value === "light" || value === "dark" ? value : null;
  } catch {
    return null;
  }
}

function systemPrefersDark(): boolean {
  return typeof window !== "undefined" && typeof window.matchMedia === "function"
    ? window.matchMedia("(prefers-color-scheme: dark)").matches
    : false;
}

/** 現在實際是亮是暗。沒選過就看系統設定。 */
export function effectiveTheme(choice: ThemeChoice): "light" | "dark" {
  return choice ?? (systemPrefersDark() ? "dark" : "light");
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** 目前手動選的主題(沒選過是 null)。切換時會重新渲染。 */
export function useThemeChoice(): ThemeChoice {
  return useSyncExternalStore(subscribe, current, () => null);
}
